import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FiArrowLeft } from "react-icons/fi";
import { PageHeader, StateBlock, Skeleton } from "../../components/UI";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormat } from "../../hooks/useFormat";
import { apiFetch } from "../../lib/api";
import { Action, Field } from "../MVP/shared";
import { Chip, Rows, Surface, Toolbar, adminErrorText } from "./ui";

type T = { id: string; email: string; name: string; subject: string; body: string; status: string; createdAt: string; category: string; context: string; number: number };

const Split = styled.div<{ $detail: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 24px;
  align-items: start;
  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
    .list {
      display: ${(p) => (p.$detail ? "none" : "block")};
    }
    .detail {
      display: ${(p) => (p.$detail ? "block" : "none")};
    }
  }
`;
const Item = styled.button<{ $on: boolean }>`
  display: grid;
  gap: 2px;
  width: 100%;
  min-height: 64px;
  padding: 10px 12px;
  border-radius: 10px;
  text-align: left;
  background: ${(p) => (p.$on ? p.theme.colors.surfaceHover : "transparent")};
  &:hover {
    background: ${(p) => (p.$on ? p.theme.colors.surfaceHover : p.theme.colors.raised)};
  }
  strong {
    font-size: 15px;
    line-height: 20px;
    overflow-wrap: anywhere;
  }
  span {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Detail = styled(Surface)`
  display: grid;
  gap: 16px;
  h2 {
    overflow-wrap: anywhere;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font: inherit;
    padding: 16px;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.navigation};
    line-height: 22px;
  }
`;

export default function SupportQueue() {
  const { t } = useLanguage();
  const f = useFormat();
  const [items, setItems] = useState<T[] | null>(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<string>("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await apiFetch("/admin/support");
      if (!res.ok) throw new Error(String(res.status));
      const d = await res.json();
      setItems(d.items || []);
    } catch (e) {
      setItems(null);
      setError((e as Error).message || "error");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const needle = q.trim().toLowerCase();
  const shown = (items || []).filter((x) => !needle || `${x.subject} ${x.name} ${x.email} ${x.body}`.toLowerCase().includes(needle));
  const cur = shown.find((x) => x.id === pick) || (items || []).find((x) => x.id === pick);

  return (
    <>
      <PageHeader title={t("adm.support")} description={t("adm.sup_scope")} actions={<Action type="button" onClick={load}>{t("adm.refresh")}</Action>} />
      {error ? (
        <Surface>
          <StateBlock kind="error" title={t("adm.load_error")} text={adminErrorText(error, t)} actionLabel={t("adm.retry")} onAction={load} />
        </Surface>
      ) : !items ? (
        <Surface>
          <div role="status" aria-label={t("adm.loading")} style={{ display: "grid", gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} $h={64} $r={10} />
            ))}
          </div>
        </Surface>
      ) : items.length === 0 ? (
        <Surface>
          <StateBlock kind="empty" title={t("adm.sup_empty")} />
        </Surface>
      ) : (
        <Split $detail={!!cur}>
          <Surface className="list" aria-label={t("adm.support")}>
            <Toolbar>
              <Field className="grow" type="search" aria-label={t("adm.sup_search")} placeholder={t("adm.sup_search")} value={q} onChange={(e) => setQ(e.target.value)} />
              <span className="count">{t("adm.total", { n: shown.length })}</span>
            </Toolbar>
            <Rows as="div" style={{ display: "grid", gap: 2 }}>
              {shown.map((x) => (
                <Item key={x.id} type="button" $on={x.id === pick} onClick={() => setPick(x.id)}>
                  <strong>
                    {t("adm.sup_number", { n: x.number })} · {x.subject}
                  </strong>
                  <span>
                    {x.name} · {f.date(x.createdAt)}
                  </span>
                </Item>
              ))}
            </Rows>
          </Surface>
          <div className="detail">
            {cur ? (
              <Detail as="article" aria-label={cur.subject}>
                <Action type="button" $variant="ghost" className="back" onClick={() => setPick("")} style={{ justifySelf: "start" }}>
                  <FiArrowLeft aria-hidden="true" />
                  {t("adm.sup_back")}
                </Action>
                <h2>{cur.subject}</h2>
                <div className="meta">
                  <span>{t("adm.sup_number", { n: cur.number })}</span>
                  <span>{cur.name}</span>
                  <span>{cur.email}</span>
                  <span>{f.date(cur.createdAt)}</span>
                  {cur.category && <Chip>{t(`support.cat.${cur.category}`)}</Chip>}
                  {cur.context && <Chip $tone="info">{t(`support.ctx.${cur.context}`)}</Chip>}
                </div>
                <pre>{cur.body}</pre>
              </Detail>
            ) : (
              <Surface>
                <p style={{ color: "inherit", opacity: 0.7 }}>{t("adm.sup_pick")}</p>
              </Surface>
            )}
          </div>
        </Split>
      )}
    </>
  );
}
